

const settings = JSON.parse(localStorage.getItem('zWarehouse')) || {}

// Get API URLs from environment variables with sensible defaults
let apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
let printApiUrl = process.env.REACT_APP_PRINTER_API_URL || 'http://localhost:3001'
let printApiKey = process.env.REACT_APP_PRINTER_API_KEY || null

// Debug logging to verify configuration
if (process.env.NODE_ENV === 'development') {
    console.log('Rest Provider Configuration:', {
        environment: settings.environment,
        apiUrl,
        printApiUrl,
        printApiKey: printApiKey ? '***configured***' : 'not configured',
        envApiUrl: process.env.REACT_APP_API_URL,
        envPrintApiUrl: process.env.REACT_APP_PRINTER_API_URL,
        envPrintApiKey: process.env.REACT_APP_PRINTER_API_KEY ? '***configured***' : 'not configured'
    })
}

// Custom httpClient with timeout support
const httpClient = (url, options = {}) => {
    const { timeout = 30000, ...fetchOptions } = options // Default 30 second timeout
    
    // Add API key to headers if this is a request to the printer API and we have an API key
    let headers = { ...fetchOptions.headers };
    if (printApiKey && url.includes(printApiUrl) && !url.includes('/generate-api-key')) {
        headers['x-api-key'] = printApiKey;
        console.log(`[DEBUG] Adding API key to request to: ${url}`);
    }
    
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)
    
    return fetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
    })
    .then(response => {
        clearTimeout(timeoutId)
        return response.json().then(json => ({
            headers: response.headers,
            json,
        }))
    })
    .catch(error => {
        clearTimeout(timeoutId)
        
        // Handle different types of errors
        if (error.name === 'AbortError') {
            throw new Error(`Request timeout after ${timeout}ms`)
        }
        
        // Handle network errors (connection refused, no internet, etc.)
        if (error.message === 'Failed to fetch' || error.message.includes('ERR_CONNECTION_REFUSED')) {
            throw new Error('Connection refused - service may be unavailable')
        }
        
        // Handle other network-related errors
        if (error.message.includes('NetworkError') || error.message.includes('ERR_NETWORK')) {
            throw new Error('Network error - check your connection')
        }
        
        // Re-throw other errors as-is
        throw error
    })
}

const HomeBatchGate = 500000
const SchoolBatchGate = 16000

// Helper function to get printers list
const getPrintersList = async () => {
    const url = `${printApiUrl}/api/discovery/printers`
    try {
        const result = await httpClient(url, { timeout: 10000 })
        return { data: result.json.printers || [] }
    } catch (error) {
        throw error
    }
}

const restProvider = {

    getPrintApiStatus: async () => {
        const url = `${printApiUrl}/api/monitoring/health`
        try {
            const result = await httpClient(url, { timeout: 10000 })
            return { data: result.json }
        } catch (error) {
            // Re-throw the error to be handled by the calling function
            throw error
        }
    },

    getPrintApiPrinters: async () => {
        const url = `${printApiUrl}/api/discovery/printers`
        try {
            const result = await httpClient(url, { timeout: 10000 })
            return { data: result.json.printers || [] }
        } catch (error) {
            // Re-throw the error to be handled by the calling function
            throw error
        }
    },

    discoverPrinters: async (ipRange = '192.168.1', startIP = 1, endIP = 254) => {
        const url = `${printApiUrl}/api/discovery/scan`
        try {
            const result = await httpClient(url, {
                method: 'POST',
                timeout: 60000, // Discovery can take longer
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ipRange,
                    startIP,
                    endIP
                })
            })
            return { data: result.json }
        } catch (error) {
            throw error
        }
    },

    addPrinter: async (printerData) => {
        // Local bridge doesn't support adding printers via API
        // Printers are discovered automatically from configured IPs
        // However, we can add metadata for a discovered printer
        throw new Error('Adding printers not supported in local bridge mode. Printers are discovered automatically from configured IP addresses.')
    },

    updatePrinter: async (ip, printerData) => {
        console.log(`[DEBUG] Attempting to update printer at IP: ${ip}`);
        console.log(`[DEBUG] Update data:`, printerData);
        console.log(`[DEBUG] Printer API URL: ${printApiUrl}`);
        
        try {
            // Call the actual printer update endpoint
            const url = `${printApiUrl}/api/discovery/printers/${ip}`;
            console.log(`[DEBUG] Update URL: ${url}`);
            
            const result = await httpClient(url, {
                method: 'PUT',
                timeout: 10000,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: printerData.name || null,
                    location: printerData.location || null
                })
            });
            
            console.log(`[DEBUG] API update response:`, result.json);
            
            if (result.json.success) {
                // Get the updated printer data from the API response
                const updatedPrinter = result.json.printer;
                console.log(`[DEBUG] Printer updated successfully via API:`, updatedPrinter);
                
                return { 
                    data: { 
                        success: true, 
                        printer: updatedPrinter,
                        message: 'Printer updated successfully via API'
                    } 
                };
            } else {
                throw new Error(result.json.error || 'Failed to update printer via API');
            }
            
        } catch (error) {
            console.log(`[DEBUG] Error in updatePrinter:`, error);
            
            // If the API update fails, fall back to local simulation for backward compatibility
            try {
                console.log(`[DEBUG] API update failed, falling back to local simulation`);
                
                // Get the current list of printers to find the one we're updating
                const printers = await getPrintersList();
                const existingPrinter = printers.data.find(p => p.ip === ip);
                
                if (!existingPrinter) {
                    throw new Error(`Printer at IP ${ip} not found in the current printer list.`);
                }
                
                // Create updated printer object
                const updatedPrinter = {
                    ...existingPrinter,
                    name: printerData.name || existingPrinter.name,
                    location: printerData.location || existingPrinter.location
                };
                
                console.log(`[DEBUG] Local simulation successful for printer:`, updatedPrinter);
                
                // Return success response with updated printer data
                return { 
                    data: { 
                        success: true, 
                        printer: updatedPrinter,
                        message: 'Printer updated successfully (local update - API endpoint failed)'
                    } 
                };
                
            } catch (fallbackError) {
                console.log(`[DEBUG] Fallback also failed:`, fallbackError);
                throw new Error(`Failed to update printer: ${error.message}`);
            }
        }
    },

    deletePrinter: async (ip) => {
        console.log(`[DEBUG] Attempting to delete printer at IP: ${ip}`);
        console.log(`[DEBUG] Printer API URL: ${printApiUrl}`);
        
        try {
            // Call the actual printer delete endpoint
            const url = `${printApiUrl}/api/discovery/printers/${ip}`;
            console.log(`[DEBUG] Delete URL: ${url}`);
            
            const result = await httpClient(url, {
                method: 'DELETE',
                timeout: 10000
            });
            
            console.log(`[DEBUG] API delete response:`, result.json);
            
            if (result.json.success) {
                console.log(`[DEBUG] Printer deleted successfully via API:`, ip);
                
                return { 
                    data: { 
                        success: true, 
                        message: 'Printer deleted successfully via API',
                        deletedPrinter: { ip }
                    } 
                };
            } else {
                throw new Error(result.json.error || 'Failed to delete printer via API');
            }
            
        } catch (error) {
            console.log(`[DEBUG] Error in deletePrinter:`, error);
            
            // If the API delete fails, fall back to local simulation for backward compatibility
            try {
                console.log(`[DEBUG] API delete failed, falling back to local simulation`);
                
                // Get the current list of printers to verify the printer exists
                const printers = await getPrintersList();
                const existingPrinter = printers.data.find(p => p.ip === ip);
                
                if (!existingPrinter) {
                    throw new Error(`Printer at IP ${ip} not found in the current printer list.`);
                }
                
                console.log(`[DEBUG] Local simulation successful for printer deletion:`, existingPrinter);
                
                // Return success response
                return { 
                    data: { 
                        success: true, 
                        message: 'Printer deleted successfully (local deletion - API endpoint failed)',
                        deletedPrinter: existingPrinter
                    } 
                };
                
            } catch (fallbackError) {
                console.log(`[DEBUG] Fallback also failed:`, fallbackError);
                throw new Error(`Failed to delete printer: ${error.message}`);
            }
        }
    },

    // =============================================================================
    // PRINTER SELECTION MANAGEMENT METHODS
    // =============================================================================

    // Get all printer selections from the API
    getPrinterSelections: async () => {
        const url = `${printApiUrl}/api/discovery/selections`;
        try {
            const result = await httpClient(url, { timeout: 10000 });
            return { data: result.json.selections || {} };
        } catch (error) {
            console.log(`[DEBUG] Error getting printer selections:`, error);
            throw error;
        }
    },

    // Set printer selection for a specific task type
    setPrinterSelection: async (taskType, printerIP) => {
        const url = `${printApiUrl}/api/discovery/selections/${taskType}`;
        try {
            const result = await httpClient(url, {
                method: 'POST',
                timeout: 10000,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ printerIP })
            });
            return { data: result.json };
        } catch (error) {
            console.log(`[DEBUG] Error setting printer selection:`, error);
            throw error;
        }
    },

    // Remove printer selection for a specific task type
    removePrinterSelection: async (taskType) => {
        const url = `${printApiUrl}/api/discovery/selections/${taskType}`;
        try {
            const result = await httpClient(url, {
                method: 'DELETE',
                timeout: 10000
            });
            return { data: result.json };
        } catch (error) {
            console.log(`[DEBUG] Error removing printer selection:`, error);
            throw error;
        }
    },

    // Clear all printer selections
    clearAllPrinterSelections: async () => {
        const url = `${printApiUrl}/api/discovery/selections`;
        try {
            const result = await httpClient(url, {
                method: 'DELETE',
                timeout: 10000
            });
            return { data: result.json };
        } catch (error) {
            console.log(`[DEBUG] Error clearing printer selections:`, error);
            throw error;
        }
    },

    getStats: async (resource, params) => {
        let url = `${apiUrl}/${resource}`
        const { status } = params

        switch (resource) {
            case 'TeacherOrder': 
                url = `${url}?stats&status=${status}`

                return httpClient(url, { timeout: 30000 }).then(({ headers, json }) => ({
                    data: json
                }))

            case 'StudentOrderKit':
                const { delivery } = params
                url = `${url}?stats&status=${status}&delivery=${delivery}`

                return httpClient(url, { timeout: 30000 }).then(({ headers, json }) => ({
                    data: json
                }))

            case 'BulkOrder':
                url = `${url}?stats&status=${status}`

                return httpClient(url, { timeout: 30000 }).then(({ headers, json }) => ({
                    data: json
                }))

            default:
                break
        }
    },

    getList: async (resource, params) => {
        let url = `${apiUrl}/${resource}`
        const { pagination: { page, perPage }, sort } = params
        const { filter: { customer }} = params
        const { filter: {
            batch,
            delivery,
            lastname,
            grade,
            school,
            city,
            province,
            release,
            list
        } } = params

        const offset = perPage * (page -1)

        switch (resource) {
            case 'StudentOrderSchool':
                url = `${apiUrl}/StudentOrderKit?delivery=school&status=On Deck&batchgate=${SchoolBatchGate}`
                url = `${url}&limit=${perPage}&offset=${offset}`
                url = `${url}&sort=${sort.field}&order=${sort.order}`

                if (batch) {
                    url = `${url}&batch=${batch}`
                }

                if (city) {
                    url = `${url}&cityname=${city}`
                }

                if (province) {
                    url = `${url}&provincename=${province}`
                }

                if (grade) {
                    url = `${url}&gradeName=${grade}`
                }

                if (school) {
                    url = `${url}&schoolname=${school}`
                }

                if (release) {
                    url = `${url}&release=${release}`
                }

                return httpClient(url, { timeout: 30000 }).then(({ headers, json}) => ({
                    data: json.rows,
                    total: +json.total
                }))

            case 'StudentOrderHome':
                url = `${apiUrl}/StudentOrderKit?delivery=home&status=On Deck&batchgate=${HomeBatchGate}`
                url = `${url}&limit=${perPage}&offset=${offset}`
                url = `${url}&sort=${sort.field}&order=${sort.order}`

                if (batch) {
                    url = `${url}&batch=${batch}`
                }

                if (school) {
                    url = `${url}&schoolname=${school}`
                }

                if (customer) {
                    const { address: { city, province }} = customer

                    if (city) {
                        url = `${url}&cityname=${city}`
                    }

                    if (province) {
                        url = `${url}&provincename=${province}`
                    }
                }

                if (grade) {
                    url = `${url}&gradeName=${grade}`
                }

                return httpClient(url, { timeout: 30000 }).then(({ headers, json}) => ({
                    data: json.rows,
                    total: +json.total
                }))

            case 'ShippedStudentOrder':
                url = `${apiUrl}/StudentOrderKit?status=Shipped`
                url = `${url}&limit=${perPage}&offset=${offset}`
                url = `${url}&sort=${sort.field}&order=${sort.order}`

                if (batch) {
                    url = `${url}&batch=${batch}`
                }

                if (delivery) {
                    url = `${url}&delivery=${delivery}`
                }

                if (province) {
                    url = `${url}&provincename=${province}`
                }

                if (city) {
                    url = `${url}&cityname=${city}`
                }

                return httpClient(url, { timeout: 30000 }).then(({ headers, json}) => ({
                    data: json.rows,
                    total: +json.total
                }))

            case 'TeacherOrder':
                url = `${url}?status=On Deck&limit=${perPage}&offset=${offset}`

                if (lastname) {
                    url = `${url}&lastname=${lastname}`
                }

                if (school) {
                    url = `${url}&school=${school}`
                }

                if (city) {
                    url = `${url}&city=${city}`
                }
                
                return httpClient(url, { timeout: 30000 }).then(({ headers, json }) => ({
                    data: json.rows,
                    total: +json.total
                }))

            case 'BulkOrder':
                url = `${url}?status=On Deck&limit=${perPage}&offset=${offset}`

                if (list) {
                    url = `${url}&list=${list.name}`
                }

                if (school) {
                    url = `${url}&school=${school.name}`
                }

                return httpClient(url, { timeout: 30000 }).then(({ headers, json}) => ({
                    data: json.rows,
                    total: +json.total
                }))

            default:
                break
        }
    },

    printLabel: async (resource, params) => {
        const { labelData, printer, labelType } = params

        // Use the new continuous endpoint for student labels
        if (labelType === 'student_labels') {
            const url = `${printApiUrl}/api/printer/print-continuous`
            const requestBody = {
                printerIP: printer,
                copies: labelData.numLabels || 1, // Use the numLabels from labelData as copies
                labelData: {
                    text: [
                        {
                            content: labelData.studentName || 'Student'
                        }
                    ]
                }
            }

            return httpClient(url, { 
                method: 'POST',
                timeout: 30000,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            }).then(({ headers, json }) => ({
                data: json
            }))
        } else {
            // Use the existing endpoint for other label types
            const url = `${printApiUrl}/api/printer/print`
            const requestBody = {
                printerIP: printer,
                labelData: labelData
            }

            // Add labelType if specified (for custom label formats)
            if (labelType) {
                requestBody.labelType = labelType
            }

            return httpClient(url, { 
                method: 'POST',
                timeout: 30000,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            }).then(({ headers, json }) => ({
                data: json
            }))
        }
    },

    printStudentLabel: async (printerIP, studentName, numLabels, zpl) => {
        console.log(`[DEBUG] printStudentLabel called with:`, { printerIP, studentName, numLabels, zpl });
        
        try {
            const url = `${printApiUrl}/api/printer/print-continuous`;
            const requestBody = {
                printerIP: printerIP,
                copies: numLabels || 1,
                labelData: {
                    text: [
                        {
                            content: studentName || 'Student'
                        }
                    ]
                }
            };

            console.log(`[DEBUG] Sending print request to: ${url}`);
            console.log(`[DEBUG] Request body:`, requestBody);

            const result = await httpClient(url, { 
                method: 'POST',
                timeout: 30000,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            console.log(`[DEBUG] Print response:`, result.json);
            
            // Return in the expected format: { data: json }
            return {
                data: result.json
            };
        } catch (error) {
            console.error(`[DEBUG] Print error:`, error);
            throw error;
        }
    },

    getConstants: async () => {
        return httpClient(`${apiUrl}/Constant`, { timeout: 30000 }).then(({ headers, json }) => ({
            data: json
        }))
    },

    // Debug function to test printer API endpoints
    testPrinterApiEndpoints: async () => {
        console.log('[DEBUG] Testing printer API endpoints...');
        console.log('[DEBUG] Printer API URL:', printApiUrl);
        
        const endpoints = [
            { name: 'Health Check', url: `${printApiUrl}/api/monitoring/health`, method: 'GET' },
            { name: 'List Printers', url: `${printApiUrl}/api/discovery/printers`, method: 'GET' },
            { name: 'Discovery Scan', url: `${printApiUrl}/api/discovery/scan`, method: 'POST' }
        ];
        
        const results = [];
        
        for (const endpoint of endpoints) {
            try {
                console.log(`[DEBUG] Testing ${endpoint.name}: ${endpoint.url}`);
                
                if (endpoint.method === 'GET') {
                    const response = await fetch(endpoint.url, { 
                        method: 'GET',
                        timeout: 5000 
                    });
                    results.push({
                        name: endpoint.name,
                        url: endpoint.url,
                        status: response.status,
                        working: response.ok,
                        error: null
                    });
                    console.log(`[DEBUG] ${endpoint.name} response:`, response.status, response.ok);
                } else if (endpoint.method === 'POST') {
                    const response = await fetch(endpoint.url, { 
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ipRange: '192.168.1', startIP: 1, endIP: 254 }),
                        timeout: 5000 
                    });
                    results.push({
                        name: endpoint.name,
                        url: endpoint.url,
                        status: response.status,
                        working: response.ok,
                        error: null
                    });
                    console.log(`[DEBUG] ${endpoint.name} response:`, response.status, response.ok);
                }
            } catch (error) {
                console.log(`[DEBUG] ${endpoint.name} error:`, error);
                results.push({
                    name: endpoint.name,
                    url: endpoint.url,
                    status: 'Error',
                    working: false,
                    error: error.message
                });
            }
        }
        
        console.log('[DEBUG] Printer API test results:', results);
        return results;
    }
}

// Make restProvider available globally for debugging
if (typeof window !== 'undefined') {
    window.restProvider = restProvider;
    window.testPrinterAPI = () => restProvider.testPrinterApiEndpoints();
    console.log('[DEBUG] restProvider and testPrinterAPI available globally for debugging');
}

export default restProvider
